import { useQuery } from '@apollo/client';
import { ICollectionLearningItem, ICollectionLearningItemsMappings } from '@ascd/witsby-components';
import { Box, Modal } from '@mui/material';
import { get, sortBy } from 'lodash';

import { LearningItems } from '@components/LearningItems';
import COLLECTION_DETAILS from '@graphql/schema/collectionDetails.graphql';
import FIND_PENDING_LEARNING_ITEM_FOR_USER from '@graphql/schema/findPendingLearningItemForUser.graphql';
import LEARNING_ITEMS_MAPPINGS from '@graphql/schema/learningItemMappings.graphql';
import LEARNING_ITEMS from '@graphql/schema/learningItems.graphql';
import { ICourse } from '@types';

interface IWhatHasUserMissed {
  userId: string;
  collectionId: string;
  handleClose: () => void;
}

const WhatHasUserMissed = ({ collectionId, userId, handleClose }: IWhatHasUserMissed) => {
  const { loading, data } = useQuery(COLLECTION_DETAILS, {
    variables: { id: collectionId },
    fetchPolicy: 'network-only',
  });

  const course: ICourse = get(data, 'collectionDetails.learningItemCollection', {});

  const { loading: learningItemsMappingsLoading, data: learningItemsMappingsData } = useQuery(
    LEARNING_ITEMS_MAPPINGS,
    {
      variables: { collectionId, parentId: '' },
      fetchPolicy: 'network-only',
    },
  );

  const { loading: pendingLearningItemForUserLoading, data: pendingLearningItemForUserData } =
    useQuery(FIND_PENDING_LEARNING_ITEM_FOR_USER, {
      variables: { collectionId, userId },
      fetchPolicy: 'network-only',
    });

  const pendingLearningItemForUser = get(
    pendingLearningItemForUserData,
    'findPendingLearningItemForUser',
    [],
  );

  let learningItemsMappings: ICollectionLearningItemsMappings[] = [];
  let learningItemsId: string[] = [];

  if (!learningItemsMappingsLoading && learningItemsMappingsData?.learningItemsMappings) {
    learningItemsMappings = learningItemsMappingsData?.learningItemsMappings;
  }

  if (!pendingLearningItemForUserLoading && pendingLearningItemForUser?.length) {
    learningItemsId = pendingLearningItemForUser.map((l: ICollectionLearningItem) => l._id);
  }

  const { loading: learningItemsLoading, data: learningItemData } = useQuery(LEARNING_ITEMS, {
    variables: { ids: learningItemsId },
    fetchPolicy: 'network-only',
  });
  let learningItemsData: ICollectionLearningItemsMappings[] | ICollectionLearningItem[] = [];
  if (!learningItemsLoading && learningItemData?.learningItems) {
    learningItemsData = learningItemData?.learningItems;
  }

  if (learningItemsData?.length && learningItemsId?.length) {
    learningItemsData = (learningItemsData as ICollectionLearningItem[]).filter(
      (l: ICollectionLearningItem) => learningItemsId.includes(l._id),
    );
    learningItemsMappings = sortBy(learningItemsMappings, 'sortingOrderIndex');

    learningItemsData = learningItemsMappings.map((l: ICollectionLearningItemsMappings) => ({
      ...(learningItemsData as ICollectionLearningItem[])
        .filter((learningItem: ICollectionLearningItem) => learningItem?.itemState === 'PUBLISHED')
        .find((d: ICollectionLearningItem) => d._id === l.learningItemId),
    })) as ICollectionLearningItemsMappings[];
    // learningItemsData = uniqBy(learningItemsData as ICollectionLearningItemsMappings[], '_id');
  } else {
    learningItemsData = [];
  }

  return (
    <Modal open onClose={handleClose}>
      <Box
        sx={{
          p: 4,
          pb: 3,
          top: '50%',
          left: '50%',
          boxShadow: 24,
          borderRadius: 2,
          overflow: 'scroll',
          position: 'absolute',
          bgcolor: 'background.paper',
          transform: 'translate(-50%, -50%)',
        }}>
        <Box
          sx={(theme) => ({
            mb: 1,
            color: theme.palette.primary.main,
            fontSize: theme.font.size.REGULAR,
            fontWeight: theme.font.weight.SEMI_BOLD,
          })}>
          What has user missed?
        </Box>
        <Box>{course.title}</Box>
        <hr />
        <Box
          sx={{
            minWidth: '50vw',
            maxHeight: '80vh',
            minHeight: '80vh',
            overflow: 'scroll',
          }}>
          {!loading &&
            !pendingLearningItemForUserLoading &&
            pendingLearningItemForUser?.length === 0 && (
              <Box
                sx={{
                  mt: 10,
                  fontSize: '1.2rem',
                  textAlign: 'center',
                }}>
                Nothing pending!
              </Box>
            )}
          {!loading && !learningItemsMappingsLoading && !learningItemsLoading && (
            <LearningItems
              course={course}
              showSectionAndChapterTitle
              dataForOtherUserId={userId}
              learningItemsData={learningItemsData}
              learningItemsMappings={learningItemsMappings}
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default WhatHasUserMissed;
